// SCR-STAFF-876 تب فاکتورها (US-INV-053، BR-INV-051): فاکتور خرید با انتخاب اقلام رسیدهای قابل فاکتور همان تأمین‌کننده (تطبیق با رسید و سفارش)،
// مقدار و بهای فاکتور، تخفیف، حمل و ارزش افزوده، نمایش اختلاف قیمت و مقدار و ثبت نهایی (تغییرناپذیر؛ اصلاح با مرجوعی).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type InvMatchableReceiptLineView, type InvPurchaseInvoiceView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, JalaliDate, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { parseJalaliInput } from '../patients/common';
import { invoiceTone, MoneyCell, procurementKeys, rialsOf, SupplierSelect } from './procurementCommon';
import { fromMilli, toMilli, useQuantityText } from './stockCommon';

export function PurchaseInvoicesTab() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const write = useWriteAccess('inv.supplier_invoice.manage');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<InvPurchaseInvoiceView | null>(null);
  const full = access.accessMode === 'Full';

  const invoices = useQuery({
    queryKey: procurementKeys.invoices(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/purchase-invoices', { params: { query: { limit: 50 } } })),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: procurementKeys.invoices(session?.tenantId) });
    void queryClient.invalidateQueries({ queryKey: procurementKeys.orders(session?.tenantId) });
    void queryClient.invalidateQueries({ queryKey: procurementKeys.payables(session?.tenantId) });
  };
  const act = useMutation({
    mutationFn: ({ invoice, verb }: { invoice: InvPurchaseInvoiceView; verb: 'post' | 'cancel' }) => {
      const options = { params: { path: { id: invoice.id } }, headers: { 'Idempotency-Key': idempotency.current() } };
      return unwrap(
        verb === 'post'
          ? tenantApi.POST('/api/v1/inventory/purchase-invoices/{id}:post', options)
          : tenantApi.POST('/api/v1/inventory/purchase-invoices/{id}:cancel', options),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setCreated(null);
      refresh();
    },
  });

  const columns: DataTableColumn<InvPurchaseInvoiceView>[] = [
    { id: 'number', headerKey: 'inv:purchasing.columns.number', cell: (row) => <bdi dir="ltr" data-testid={`invoice-${row.number}`}>{row.number}</bdi> },
    { id: 'supplierNumber', headerKey: 'inv:purchasing.columns.supplier_invoice', cell: (row) => <bdi dir="ltr">{row.supplierInvoiceNumber}</bdi> },
    {
      id: 'status',
      headerKey: 'inv:purchasing.columns.status',
      cell: (row) => (
        <span data-testid={`invoice-status-${row.number}`}>
          <StatusChip tone={invoiceTone(row.status)} labelKey={`inv:invoice_status.${row.status}`} />
        </span>
      ),
    },
    { id: 'supplier', headerKey: 'inv:purchasing.columns.supplier', cell: (row) => row.supplierName },
    { id: 'total', headerKey: 'inv:purchasing.columns.total', cell: (row) => <MoneyCell value={row.total} /> },
    {
      id: 'difference',
      headerKey: 'inv:purchasing.columns.price_difference',
      cell: (row) => (
        <span data-testid={`invoice-difference-${row.number}`}>
          <MoneyCell value={row.inventoryAdjustment} />
        </span>
      ),
    },
    { id: 'open', headerKey: 'inv:purchasing.columns.open', cell: (row) => <MoneyCell value={row.open} /> },
    { id: 'due', headerKey: 'inv:purchasing.columns.due_date', cell: (row) => <JalaliDate value={row.dueDate} /> },
    {
      id: 'actions',
      headerKey: 'inv:purchasing.columns.actions',
      cell: (row) =>
        row.status === 'Draft' ? (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" variant="outlined" disabled={!full || !write.canWrite || act.isPending} onClick={() => act.mutate({ invoice: row, verb: 'post' })} data-testid={`invoice-post-${row.number}`}>
              {t('inv:purchasing.post_invoice')}
            </Button>
            <Button size="small" color="inherit" disabled={!write.canWrite || act.isPending} onClick={() => act.mutate({ invoice: row, verb: 'cancel' })}>
              {t('inv:purchasing.cancel')}
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row">
        <Button variant="contained" onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="invoice-create">
          {t('inv:purchasing.add_invoice')}
        </Button>
      </Stack>
      {act.error && <Alert severity="error">{t('inv:purchasing.action_failed')}</Alert>}
      {created && created.warnings.length > 0 && (
        <Alert severity="warning" onClose={() => setCreated(null)} data-testid="invoice-warnings">
          {created.warnings.map((warning) => t(`inv:purchasing.warnings.${warning}`)).join(t('common:list_separator'))}
        </Alert>
      )}
      <DataTable
        captionKey="inv:purchasing.invoices_caption"
        columns={columns}
        rows={invoices.data?.items}
        getRowId={(row) => row.id}
        isLoading={invoices.isPending}
        error={invoices.error}
        onRetry={() => void invoices.refetch()}
        emptyTitleKey="inv:purchasing.invoices_empty"
      />
      {creating && (
        <InvoiceDialog
          onClose={() => setCreating(false)}
          onSaved={(invoice) => {
            setCreating(false);
            setCreated(invoice);
            refresh();
          }}
        />
      )}
    </Stack>
  );
}

interface InvoiceLineState {
  selected: boolean;
  quantity: string;
  unitPrice: string;
}

function InvoiceDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (invoice: InvPurchaseInvoiceView) => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const quantity = useQuantityText();
  const [supplierId, setSupplierId] = useState('');
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [discount, setDiscount] = useState('');
  const [freight, setFreight] = useState('');
  const [vat, setVat] = useState('');
  const [lines, setLines] = useState<Record<string, InvoiceLineState>>({});
  const matchable = useQuery({
    queryKey: ['inventory', 'purchase-invoices', session?.tenantId, 'matchable', supplierId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/purchase-invoices/matchable-lines', { params: { query: { supplierId } } })),
    enabled: Boolean(supplierId),
  });
  const stateOf = (row: InvMatchableReceiptLineView): InvoiceLineState =>
    lines[row.receiptLineId] ?? { selected: false, quantity: String(fromMilli(row.invoiceableMilli)), unitPrice: String(row.receiptUnitCost.amount) };
  const update = (row: InvMatchableReceiptLineView, patch: Partial<InvoiceLineState>) => setLines((current) => ({ ...current, [row.receiptLineId]: { ...stateOf(row), ...patch } }));
  const chosen = (matchable.data?.items ?? []).filter((row) => stateOf(row).selected);
  const money = (text: string) => {
    const value = rialsOf(text);
    return value === null ? null : { amount: value, currency: 'IRR' };
  };

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/purchase-invoices', {
          body: {
            supplierId,
            supplierInvoiceNumber: supplierInvoiceNumber.trim(),
            invoiceDate: parseJalaliInput(invoiceDate) ?? '',
            dueDate: parseJalaliInput(dueDate) ?? null,
            discount: money(discount),
            freight: money(freight),
            vat: money(vat),
            invoiceFileId: null,
            note: null,
            lines: chosen.map((row) => ({
              receiptLineId: row.receiptLineId,
              quantityMilli: toMilli(stateOf(row).quantity) ?? 0,
              unit: null,
              unitPrice: { amount: rialsOf(stateOf(row).unitPrice) ?? 0, currency: 'IRR' },
            })),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
  });

  const columns: DataTableColumn<InvMatchableReceiptLineView>[] = [
    {
      id: 'pick',
      headerKey: 'inv:purchasing.columns.pick',
      cell: (row) => (
        <Checkbox
          checked={stateOf(row).selected}
          onChange={(event) => update(row, { selected: event.target.checked })}
          slotProps={{ input: { 'aria-label': `${row.receiptNumber} ${row.itemName}`, 'data-testid': `invoice-line-pick-${row.receiptNumber}-${row.lineNo}` } as object }}
        />
      ),
    },
    {
      id: 'receipt',
      headerKey: 'inv:purchasing.columns.receipt',
      cell: (row) => (
        <Stack spacing={0.25}>
          <bdi dir="ltr">{row.receiptNumber}</bdi>
          {row.purchaseOrderNumber && (
            <Typography variant="caption">
              <bdi dir="ltr">{row.purchaseOrderNumber}</bdi>
            </Typography>
          )}
        </Stack>
      ),
    },
    { id: 'item', headerKey: 'inv:stock.columns.item', cell: (row) => `${row.itemName}${row.batchNumber ? ` (${row.batchNumber})` : ''}` },
    { id: 'invoiceable', headerKey: 'inv:purchasing.columns.invoiceable', cell: (row) => quantity(row.invoiceableMilli, row.baseUnit) },
    { id: 'receiptCost', headerKey: 'inv:purchasing.columns.receipt_cost', cell: (row) => <MoneyCell value={row.receiptUnitCost} /> },
    {
      id: 'quantity',
      headerKey: 'inv:documents.quantity',
      cell: (row) => (
        <TextField
          size="small"
          value={stateOf(row).quantity}
          onChange={(event) => update(row, { quantity: event.target.value })}
          disabled={!stateOf(row).selected}
          sx={{ width: 110 }}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'aria-label': t('inv:documents.quantity'), 'data-testid': `invoice-line-quantity-${row.receiptNumber}-${row.lineNo}` } }}
        />
      ),
    },
    {
      id: 'price',
      headerKey: 'inv:purchasing.columns.invoice_price',
      cell: (row) => (
        <TextField
          size="small"
          value={stateOf(row).unitPrice}
          onChange={(event) => update(row, { unitPrice: event.target.value })}
          disabled={!stateOf(row).selected}
          sx={{ width: 150 }}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'aria-label': t('inv:purchasing.columns.invoice_price'), 'data-testid': `invoice-line-price-${row.receiptNumber}-${row.lineNo}` } }}
        />
      ),
    },
  ];

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-876-INVOICE-FORM"
      titleKey="inv:purchasing.invoice_title"
      onSubmit={() => (supplierId && chosen.length > 0 ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={chosen.length > 0}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="lg"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SupplierSelect
          value={supplierId}
          onChange={(id) => {
            setSupplierId(id);
            setLines({});
          }}
          testId="invoice-supplier"
          required
        />
        <TextField size="small" required label={t('inv:purchasing.supplier_invoice_number')} value={supplierInvoiceNumber} onChange={(event) => setSupplierInvoiceNumber(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'invoice-number' } }} />
        <TextField
          size="small"
          required
          label={t('inv:purchasing.invoice_date')}
          placeholder="1405/07/13"
          value={invoiceDate}
          onChange={(event) => setInvoiceDate(event.target.value)}
          error={Boolean(invoiceDate.trim()) && !parseJalaliInput(invoiceDate)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'invoice-date' } }}
        />
        <TextField
          size="small"
          label={t('inv:purchasing.due_date')}
          helperText={t('inv:purchasing.due_date_help')}
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          error={Boolean(dueDate.trim()) && !parseJalaliInput(dueDate)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'invoice-due-date' } }}
        />
      </Stack>
      {supplierId ? (
        <DataTable
          captionKey="inv:purchasing.matchable_caption"
          columns={columns}
          rows={matchable.data?.items}
          getRowId={(row) => row.receiptLineId}
          isLoading={matchable.isPending}
          error={matchable.error}
          emptyTitleKey="inv:purchasing.matchable_empty"
        />
      ) : (
        <Alert severity="info">{t('inv:purchasing.choose_supplier')}</Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField size="small" label={t('inv:purchasing.discount')} value={discount} onChange={(event) => setDiscount(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'invoice-discount' } }} />
        <TextField size="small" label={t('inv:purchasing.freight')} value={freight} onChange={(event) => setFreight(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'invoice-freight' } }} />
        <TextField size="small" label={t('inv:purchasing.vat')} value={vat} onChange={(event) => setVat(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'invoice-vat' } }} />
      </Stack>
      <Alert severity="info">{t('inv:purchasing.invoice_note')}</Alert>
    </FormDialog>
  );
}
